import { ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AppDrawer from "../../shared/ui/AppDrawer";
import StatusChip from "../../shared/ui/StatusChip";
import SlaBadge, { SlaBadgeValue } from "./SlaBadge";
import { TimelineItem, useTimeline } from "./api";

export type HistoryRow = {
  id: number;
  doctor_name?: string | null;
  company_name?: string | null;
  status?: string | null;
  sla?: SlaBadgeValue | null;
};

const HOUR_MS = 3_600_000;
const pad = (n: number) => String(n).padStart(2, "0");

// `at` vem em ISO BRT; com Z/offset, converte para o relógio de Brasília (UTC−3 fixo), sem depender do fuso da máquina.
const brtMs = (at: string) => (/[zZ]|[+-]\d{2}:?\d{2}$/.test(at) ? Date.parse(at) - 3 * HOUR_MS : Date.parse(`${at.replace(" ", "T")}Z`));

export function formatBrtDateTime(at: string): string {
  const ms = brtMs(at);
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

const sortKey = (at: string) => {
  const ms = brtMs(at);
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
};

const CREATED_SUFFIX: Record<string, string> = { split: " na divisão", merge: " no agrupamento" };
const MOVE_LABEL: Record<string, string> = { split: "Divisão", merge: "Agrupamento" };

function headline(item: TimelineItem): ReactNode {
  if (item.type === "feedback") {
    if (!item.reason) return "Comentário";
    return `Motivo: ${item.reason}${item.reasonActive === false ? " (inativo)" : ""}`;
  }
  if (item.type === "move") return MOVE_LABEL[item.source ?? ""] ?? "Movimentação de plantões";
  if (!item.from) {
    return (
      <>
        <span>{`Pacote criado${CREATED_SUFFIX[item.source ?? ""] ?? ""}`}</span>
        {item.to && <StatusChip status={item.to} />}
      </>
    );
  }
  return (
    <>
      <StatusChip status={item.from} />
      <ArrowForwardIcon sx={{ fontSize: 16, color: "text.secondary" }} aria-label="para" />
      {item.to && <StatusChip status={item.to} />}
      {item.source === "backfill" && (
        <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
          (aproximado)
        </Typography>
      )}
    </>
  );
}

function Timeline({ items }: { items: TimelineItem[] }) {
  // Ordem cronológica; empate mantém a ordem do BE (status antes de feedback).
  const sorted = items.map((item, i) => ({ item, i })).sort((a, b) => sortKey(a.item.at) - sortKey(b.item.at) || a.i - b.i);
  return (
    <List disablePadding sx={{ position: "relative", pl: 2, ml: 0.75, borderLeft: "2px solid", borderColor: "divider" }}>
      {sorted.map(({ item, i }) => (
        <ListItem
          key={i}
          disableGutters
          sx={{
            position: "relative",
            display: "block",
            py: 1,
            "&::before": {
              content: '""',
              position: "absolute",
              left: "-22px",
              top: "16px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              bgcolor: item.type === "status" ? "primary.main" : item.type === "feedback" ? "#e8a317" : "#64748b",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, fontSize: 13.5, fontWeight: 600, color: "primary.main" }}>
            {headline(item)}
          </Box>
          {item.text && <Typography sx={{ fontSize: 13, mt: 0.25 }}>{`“${item.text}”`}</Typography>}
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
            {`${item.actor ?? "Sistema"} · ${formatBrtDateTime(item.at)}`}
          </Typography>
        </ListItem>
      ))}
    </List>
  );
}

/** Histórico do pacote: só leitura (CA-F1a-07), visível para Leitura, Operador e Admin. */
export default function StatusHistoryDrawer({ open, row, onClose }: { open: boolean; row: HistoryRow; onClose: () => void }) {
  const { data, isPending, isError, refetch } = useTimeline(row.id, open);
  const context = [row.doctor_name, row.company_name].filter(Boolean).join(" · ");
  return (
    <AppDrawer open={open} onClose={onClose} title={`Histórico do pacote #${row.id}`} context={context || undefined}>
      {(row.status || row.sla) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {row.status && <StatusChip status={row.status} />}
          {row.sla && <SlaBadge sla={row.sla} />}
        </Box>
      )}
      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Tentar de novo
            </Button>
          }
        >
          Não foi possível carregar o histórico.
        </Alert>
      ) : isPending ? (
        <Box aria-busy="true">
          {[0, 1, 2, 3].map((k) => (
            <Skeleton key={k} data-testid="timeline-loading" height={44} />
          ))}
        </Box>
      ) : data && data.length > 0 ? (
        <Timeline items={data} />
      ) : (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Sem eventos registrados.</Typography>
      )}
    </AppDrawer>
  );
}
